import { useEffect, useMemo, useState } from 'react'
import Barcodes from 'pages/Barcodes'
import { Button, ButtonGroup, Paper, TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import GlobalBarcodes from '../GlobalBarcodes'
import MarketGate from 'components/MarketGate'
import { Can } from 'config/ability'
import { useNavigate } from 'react-router-dom'
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined'
import { Add } from '@mui/icons-material'

const BarcodesTabs = (): JSX.Element => {
  const { t } = useTranslation()
  type TabName = 'market' | 'global'
  const [selectedTab, setSelectedTab] = useState<TabName>('market')
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')

  const tabs = (selectedTab: TabName) => {
    if (selectedTab === 'market') {
      return (
        <Paper sx={{ p: 4 }}>
          <MarketGate
            key="translations"
            allOption
            componentProps={{ search }}
            Component={Barcodes}
            titleTranslationCode="barcodes.title"
            descriptionCode="barcodes.list"
            actions={
              <>
                <TextField
                  onChange={e => {
                    setSearch(e.target.value)
                  }}
                  label={t('barcodes.searchByCode')}
                  value={search}
                  size="small"
                />
                <Can I="create" a="Barcodes">
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={(): void => {
                      navigate('/barcodes/add')
                    }}
                    startIcon={<Add />}
                  >
                    {t('common.add')}
                  </Button>
                </Can>
                <Can I="delete" a="Barcodes">
                  <Button
                    variant="contained"
                    form="barcodes-table-form"
                    type="submit"
                    color="error"
                    startIcon={<DeleteForeverOutlinedIcon />}
                  >
                    {t('common.delete')}
                  </Button>
                </Can>
              </>
            }
          />
        </Paper>
      )
    }
    return <GlobalBarcodes />
  }

  const changeTab = (name: TabName): void => {
    setSelectedTab(name)
    setSearchParams({ tab: name })
  }

  useEffect(() => {
    const selected = searchParams.get('tab')

    if (selected !== null) {
      setSelectedTab(selected as TabName)
    }
  }, [searchParams])

  const tab = useMemo(() => tabs(selectedTab), [selectedTab, search])

  return (
    <>
      <ButtonGroup sx={{ mb: 2 }} size="small" color="secondary">
        <Button
          variant={selectedTab === 'market' ? 'contained' : 'outlined'}
          onClick={() => {
            changeTab('market')
          }}
        >
          {t('navigation.barcodes')}
        </Button>
        <Button
          variant={selectedTab === 'global' ? 'contained' : 'outlined'}
          onClick={() => {
            changeTab('global')
          }}
        >
          {t('navigation.globalBarcodes')}
        </Button>
      </ButtonGroup>
      {tab}
    </>
  )
}

export default BarcodesTabs
